import { AppShell as MAppShell, Button, Center, Group, Loader, Text } from "@mantine/core";
import { IconLogin } from "@tabler/icons-react";
import { Link, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext.js";
import { AppShell } from "../../layout/AppShell.js";
import { LanguageSwitcher } from "../../components/LanguageSwitcher.js";

/**
 * Shell for the documentation routes, branched on session state.
 *
 * Waits for the auth check to settle before picking a shell. `useAuth()`
 * resolves `user: null` both while `/api/auth/me` is still in flight and
 * once it's confirmed there's no session — indistinguishable states if we
 * only looked at `user`. Branching on `user` alone would render the
 * anonymous chrome first for an already-logged-in visitor on a cold load
 * (a bookmark, a shared link, a reload), then swap to `<AppShell />` once
 * the query resolves — a different component at the same position, so
 * React unmounts and remounts the whole subtree, including the document
 * iframe, flashing it and losing scroll position. Gating on `loading`
 * first — matching `RequireAuth`'s spinner treatment — means the shell
 * type is decided exactly once, after the session is known.
 *
 * Authenticated visitors get the exact same chrome as the rest of the app —
 * header, sidebar navigation, main content — by rendering the real
 * `AppShell` (rather than re-approximating its markup here), so the two
 * never drift out of sync and a logged-in reader never loses in-app
 * navigation while reading documentation. `AppShell`'s own `<Outlet />`
 * still resolves to this route's matched child (`DokumentationPage` or
 * `DocumentPage`): `Outlet` follows the route tree via context, not the
 * component tree, so nesting it one component deeper changes nothing.
 *
 * Anonymous visitors get a minimal reader chrome instead: no sidebar (there
 * is nothing they're cleared to navigate to without a session), just a way
 * back to the document list and a way to log in.
 */
export function PublicDocsLayout() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();

  if (loading) return <Center h="100vh"><Loader /></Center>;
  if (user) return <AppShell />;

  return (
    <MAppShell header={{ height: 56 }} padding="md">
      <MAppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Text fw={600} component={Link} to="/dokumentation" style={{ textDecoration: "none" }}>
            {t("docs.title")}
          </Text>
          <Group gap="xs">
            <LanguageSwitcher />
            <Button component={Link} to="/login" variant="light" leftSection={<IconLogin size={16} />}>
              {t("docs.login")}
            </Button>
          </Group>
        </Group>
      </MAppShell.Header>
      <MAppShell.Main><Outlet /></MAppShell.Main>
    </MAppShell>
  );
}
